import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';

import folderImg from 'assets/brand/folder.png';
import { formatAmount } from 'common/formatAmount';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineModal, {
  SunshineModalProps,
} from 'components/_core/SunshineModal';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';

import { SetTaxForecastPeriodAmountPaidDocument } from './graphql/setTaxForecastPeriodAmountPaid.gql';
import locales from './locales';
import {
  MarkAsPaidModalSchema,
  markAsPaidModalSchema,
} from './markAsPaidModal.schema';

interface MarkAsPaidModalProps extends Omit<SunshineModalProps, 'children'> {
  taxForecastPeriodId: string;
  periodLabel: string;
  amount: number;
}

const MarkAsPaidModal: FC<MarkAsPaidModalProps> = ({
  amount,
  isOpen,
  onClose,
  periodLabel,
  taxForecastPeriodId,
}) => {
  const { showToast } = useToast();

  const [setTaxForecastPeriodAmountPaidMutation, { loading }] = useMutation(
    SetTaxForecastPeriodAmountPaidDocument,
  );

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<MarkAsPaidModalSchema>({
    defaultValues: { amountPaid: amount },
    resolver: zodResolver(markAsPaidModalSchema),
  });

  useEffect(() => {
    reset();
  }, [isOpen, reset]);

  const onSubmit = handleSubmit(({ amountPaid }) => {
    setTaxForecastPeriodAmountPaidMutation({
      onCompleted: () => {
        showToast({ message: locales.successMessage, type: 'success' });
        onClose();
      },
      onError: () =>
        showToast({ message: locales.errorMessage, type: 'error' }),
      variables: {
        input: { amountPaid, taxForecastPeriodId },
      },
    });
  });

  return (
    <SunshineModal isOpen={isOpen} onClose={onClose}>
      <SunshineModal.Header></SunshineModal.Header>

      <SunshineModal.Body>
        <SunshineModal.Body.Image height="144px" src={folderImg} />

        <SunshineModal.Title>{locales.title}</SunshineModal.Title>

        <SunshineCard marginBottom="space-16">
          <Text variant="primary">{periodLabel}</Text>

          <SunshineCard.Slot name="action">
            <Text fontWeight="weight-500" variant="primary">
              {formatAmount({ forcePlusSign: false, value: amount })}
            </Text>
          </SunshineCard.Slot>
        </SunshineCard>

        <Form.Field
          error={errors.amountPaid?.message}
          label={locales.inputLabel}
        >
          <Form.Input
            {...register('amountPaid', { valueAsNumber: true })}
            defaultValue={amount}
            rightIcon="euro"
            type="number"
          />
        </Form.Field>
      </SunshineModal.Body>

      <SunshineModal.Footer>
        <Button isLoading={loading} onClick={onSubmit}>
          {locales.confirm}
        </Button>
      </SunshineModal.Footer>
    </SunshineModal>
  );
};

export default MarkAsPaidModal;
